import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Box, Slide } from '@mui/material';
import Toast from './Toast';
import { TOAST_DEFAULT_DURATION, TOAST_VARIANTS } from './toastConfig';

const ToastContext = createContext(null);

/** Ref imperativo para `toast.show()` / `toast.clear()` fuera de React. */
let externalShow = null;
let externalClear = null;

function normalizeOptions(input, overrides = {}) {
  if (typeof input === 'string') {
    return { message: input, ...overrides };
  }
  return { ...input, ...overrides };
}

/**
 * Provider global de toasts. Montar una sola vez cerca de la raíz (App).
 *
 * Uso:
 *   const { showToast } = useToast();
 *   showToast({ type: 'success', message: 'Lista creada con éxito.' });
 *
 *   import { toast } from '../Components/Toast';
 *   toast.success('Lista creada con éxito.');
 */
export function ToastProvider({ children }) {
  const [items, setItems] = useState([]);
  const timersRef = useRef(new Map());

  const dismiss = useCallback((id) => {
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
    setItems((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const clear = useCallback(() => {
    timersRef.current.forEach((timer) => clearTimeout(timer));
    timersRef.current.clear();
    setItems([]);
  }, []);

  const showToast = useCallback(
    (input = {}) => {
      const opts = normalizeOptions(input);
      const type = TOAST_VARIANTS[opts.type] ? opts.type : 'info';
      const message = opts.message ?? '';
      const title = typeof opts.title === 'string' ? opts.title.trim() : '';
      if (!message && !title) return null;

      const closable = opts.closable !== false;
      const duration =
        opts.duration === undefined || opts.duration === null
          ? TOAST_DEFAULT_DURATION
          : opts.duration;

      const id = opts.id || `toast-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const actionLabel = opts.actionLabel || null;
      const onAction = typeof opts.onAction === 'function' ? opts.onAction : null;

      setItems((prev) => {
        // Un toast a la vez evita apilar encima del mapa; si hay otro, lo reemplaza.
        prev.forEach((t) => {
          const timer = timersRef.current.get(t.id);
          if (timer) {
            clearTimeout(timer);
            timersRef.current.delete(t.id);
          }
        });
        return [{ id, type, title: title || null, message, closable, duration, actionLabel, onAction }];
      });

      if (duration > 0) {
        const timer = setTimeout(() => dismiss(id), duration);
        timersRef.current.set(id, timer);
      }

      return id;
    },
    [dismiss]
  );

  useEffect(() => {
    externalShow = showToast;
    externalClear = clear;
    const timers = timersRef.current;
    return () => {
      externalShow = null;
      externalClear = null;
      timers.forEach((timer) => clearTimeout(timer));
      timers.clear();
    };
  }, [showToast, clear]);

  const value = useMemo(
    () => ({
      showToast,
      dismissToast: dismiss,
    }),
    [showToast, dismiss]
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <Box
        sx={{
          position: 'fixed',
          top: { xs: 98, sm: 102 },
          right: { xs: 16, sm: 74 },
          left: 'auto',
          zIndex: 2000,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          pointerEvents: 'none',
        }}
      >
        {items.map((item) => (
          <Slide key={item.id} direction="left" in appear mountOnEnter unmountOnExit>
            <Box>
              <Toast
                type={item.type}
                title={item.title}
                message={item.message}
                closable={item.closable}
                actionLabel={item.actionLabel}
                onAction={
                  item.onAction
                    ? () => {
                        item.onAction();
                        dismiss(item.id);
                      }
                    : undefined
                }
                onClose={() => dismiss(item.id)}
              />
            </Box>
          </Slide>
        ))}
      </Box>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast debe usarse dentro de <ToastProvider>');
  }
  return ctx;
}

export const toast = {
  show: (opts) => externalShow?.(opts) ?? null,
  clear: () => externalClear?.(),
  success: (message, opts) =>
    externalShow?.(normalizeOptions(message, { type: 'success', ...opts })) ?? null,
  error: (message, opts) =>
    externalShow?.(normalizeOptions(message, { type: 'error', ...opts })) ?? null,
  warning: (message, opts) =>
    externalShow?.(normalizeOptions(message, { type: 'warning', ...opts })) ?? null,
  info: (message, opts) =>
    externalShow?.(normalizeOptions(message, { type: 'info', ...opts })) ?? null,
};
